import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from 'react-slick';
import styles from './HomePage.module.css';
import ServiceCard from '../components/ServiceCard/ServiceCard';
import TestimonialCard from '../components/TestimonialCard/TestimonialCard';
import StatsCounter from '../components/StatsCounter/StatsCounter';
import VisualCarousel from '../components/AnimatedVisuals/VisualCarousel';
import GraphVisual from '../components/GraphVisual/GraphVisual';
import ParticleBackground from '../components/InteractiveEffects/ParticleBackground';
import SEO from '../components/SEO/SEO';
import StructuredData from '../components/StructuredData/StructuredData';
import { servicesData } from '../data/servicesData';
import { testimonialsData } from '../data/testimonialsData';

const HomePage = () => {
  const featuredServices = servicesData.slice(0, 4);
  const sectionRefs = useRef([]);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  
  const typewriterTexts = useMemo(() => [
    'Transform Your Business.',
    'Scale Your Operations.',
    'Innovate Faster.',
    'Dominate Your Market.'
  ], []);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mouse position tracking for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Typewriter effect
  useEffect(() => {
    const currentText = typewriterTexts[currentTextIndex];
    let timeoutId;

    if (isTyping) {
      if (typedText.length < currentText.length) {
        timeoutId = setTimeout(() => {
          setTypedText(currentText.slice(0, typedText.length + 1));
        }, 100);
      } else {
        timeoutId = setTimeout(() => {
          setIsTyping(false);
        }, 2000);
      }
    } else {
      if (typedText.length > 0) {
        timeoutId = setTimeout(() => {
          setTypedText(typedText.slice(0, -1));
        }, 50);
      } else {
        setIsTyping(true);
        setCurrentTextIndex((prev) => (prev + 1) % typewriterTexts.length);
      }
    }

    return () => clearTimeout(timeoutId);
  }, [typedText, isTyping, currentTextIndex, typewriterTexts]);

  useEffect(() => {
    // Disable Intersection Observer on mobile to prevent layout shifts
    const isMobile = window.innerWidth <= 767;
    
    if (isMobile) {
      // On mobile, just add visible class immediately
      sectionRefs.current.forEach(ref => {
        if (ref) {
          ref.classList.add(styles.visible);
        }
      });
      return;
    }

    // Enhanced Intersection Observer with stagger animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting && !entry.target.classList.contains(styles.visible)) {
          setTimeout(() => {
            entry.target.classList.add(styles.visible);
            // Add stagger effect to children
            const children = entry.target.querySelectorAll('[data-animate]');
            children.forEach((child, childIndex) => {
              setTimeout(() => {
                child.classList.add(styles.animated);
              }, childIndex * 100);
            });
          }, index * 100);
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const timeoutId = setTimeout(() => {
      sectionRefs.current.forEach(ref => {
        if (ref) observer.observe(ref);
      });
    }, 100);

    const currentRefs = sectionRefs.current;
    return () => {
      clearTimeout(timeoutId);
      currentRefs.forEach(ref => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    arrows: true,
  };

  return (
    <div className={styles.homePage}>
      <SEO 
        title="SolveDot Labs | Top Software Development Company | AI, IoT, Robotics & Web Development Services"
        description="SolveDot Labs (SolveDot Labs Pvt. Ltd.) - Leading software development company specializing in AI solutions, website development, IoT systems, robotics, and custom software development. Trusted by 200+ clients with 99% uptime guarantee. Based in Greater Noida, India."
        keywords="solve, solvedot, solvedotlabs, solvedot labs, solve dot labs, solvedot labs pvt ltd, SolveDot Labs, AI, Software, Software Development, website development, web development, iot, robotics, software company, AI Solutions, Software Development Company, Custom Software Development, IoT Solutions, Robotics Solutions, Web Development Services, Software Services"
      />
      <StructuredData type="homepage" />
      <StructuredData type="website" />
      <StructuredData type="faq" />
      {/* Scroll Progress Indicator */}
      <div className={styles.scrollProgressBar}>
        <div 
          className={styles.scrollProgressFill}
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>

      {/* --- Enhanced Hero Section with Gradient Background --- */}
      <section className={`${styles.hero} ${styles.heroAnimated}`}>
        <ParticleBackground />
        <div className={styles.heroBackground}>
          <div className={styles.gradientOrb1}></div>
          <div className={styles.gradientOrb2}></div>
          <div className={styles.gradientOrb3}></div>
        </div>
        <div className={styles.heroContent} ref={el => sectionRefs.current[0] = el}>
          <div className={styles.heroText}>
            <h1 className={styles.title}>
              <span className={styles.titleLine1}>Don't Just Compete.</span>
              <span className={styles.titleLine2}>
                <span className={styles.typewriterText}>{typedText}</span>
                <span className={styles.cursor}>|</span>
              </span>
            </h1>
            <p className={styles.subtitle}>
              We build the guaranteed, almost zero-downtime software that transforms your biggest challenges into your greatest advantages.
            </p>
            <div className={styles.ctaGroup}>
              <Link to="/services" className={styles.ctaButton}>
                <span>Explore Services</span>
                <span className={styles.arrow}>→</span>
              </Link>
              <Link to="/contact" className={styles.ctaButtonSecondary}>
                Get Started
              </Link>
            </div>
          </div>
          <div 
            className={styles.heroVisual}
            style={{
              transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`
            }}
          >
            <div className={styles.floatingCard}>
              <VisualCarousel />
            </div>
          </div>
        </div>
        <div 
          className={styles.scrollIndicator}
          onClick={() => {
            window.scrollTo({
              top: window.innerHeight,
              behavior: 'smooth'
            });
          }}
          role="button"
          tabIndex={0}
          aria-label="Scroll down"
        >
          <div className={styles.mouse}>
            <div className={styles.wheel}></div>
          </div>
        </div>
      </section>

      {/* --- Stats Section --- */}
      <section className={`${styles.section} ${styles.statsSection}`} ref={el => sectionRefs.current[1] = el}>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>⚡</div>
            <StatsCounter end={99} suffix="%" />
            <p className={styles.statLabel}>Uptime Guarantee</p>
            <GraphVisual data={[85, 92, 95, 98, 99, 99]} label="Uptime Trend" />
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>🚀</div>
            <StatsCounter end={20} suffix="+" />
            <p className={styles.statLabel}>Projects Delivered</p>
            <GraphVisual data={[2, 3, 6, 8, 12, 20]} label="Growth" />
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>😊</div>
            <StatsCounter end={20} suffix="+" />
            <p className={styles.statLabel}>Happy Clients</p>
            <GraphVisual data={[1, 4, 6, 10, 15, 20]} label="Satisfaction" />
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>⭐</div>
            <StatsCounter end={4} suffix="+" />
            <p className={styles.statLabel}>Years of Excellence</p>
            <GraphVisual data={[1, 2, 3, 4]} label="Experience" />
          </div>
        </div>
      </section>

      {/* --- Technology Stack Section --- */}
      <section className={`${styles.section} ${styles.techStackSection}`} ref={el => sectionRefs.current[2] = el}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Technology Stack</span>
          <h2 className={styles.sectionTitle}>Built with Cutting-Edge Technologies</h2>
          <p className={styles.sectionDescription}>
            We leverage the latest and most powerful technologies to deliver exceptional results
          </p>
        </div>
        <div className={styles.techGrid}>
          {[
            { 
              title: 'Frontend Frameworks', 
              icon: '⚛️', 
              description: 'Modern, responsive UI frameworks for seamless user experiences',
              technologies: 'React, Vue, Angular, Next.js'
            },
            { 
              title: 'Backend Technologies', 
              icon: '🟢', 
              description: 'Robust server-side solutions for scalable applications',
              technologies: 'Node.js, Python, Java, .NET'
            },
            { 
              title: 'Cloud Platforms', 
              icon: '☁️', 
              description: 'Enterprise-grade cloud infrastructure and services',
              technologies: 'AWS, Azure, GCP, Multi-Cloud'
            },
            { 
              title: 'DevOps & CI/CD', 
              icon: '🐳', 
              description: 'Automated deployment pipelines and containerization',
              technologies: 'Docker, Kubernetes, Jenkins, GitLab CI'
            },
            { 
              title: 'AI & Machine Learning', 
              icon: '🧠', 
              description: 'Intelligent systems powered by advanced ML algorithms',
              technologies: 'TensorFlow, PyTorch, OpenAI, Custom Models'
            },
            { 
              title: 'Databases', 
              icon: '🍃', 
              description: 'Scalable data storage and management solutions',
              technologies: 'MongoDB, PostgreSQL, Redis, Elasticsearch'
            },
            { 
              title: 'API & Integration', 
              icon: '📊', 
              description: 'RESTful APIs, GraphQL, and seamless third-party integrations',
              technologies: 'REST, GraphQL, WebSockets, Microservices'
            },
            { 
              title: 'Mobile Development', 
              icon: '📱', 
              description: 'Native and cross-platform mobile applications',
              technologies: 'React Native, Flutter, iOS, Android'
            },
            { 
              title: 'Security & Compliance', 
              icon: '🔒', 
              description: 'Enterprise-grade security and regulatory compliance',
              technologies: 'OAuth, SSL/TLS, GDPR, SOC 2'
            },
            { 
              title: 'Analytics & BI', 
              icon: '📈', 
              description: 'Data analytics and business intelligence platforms',
              technologies: 'Power BI, Tableau, Custom Dashboards'
            },
            { 
              title: 'IoT & Edge Computing', 
              icon: '💡', 
              description: 'Connected devices and edge computing solutions',
              technologies: 'MQTT, Edge Devices, Real-time Processing'
            },
            { 
              title: 'Blockchain & Web3', 
              icon: '⛓️', 
              description: 'Decentralized applications and blockchain solutions',
              technologies: 'Ethereum, Smart Contracts, Web3 Integration'
            },
          ].map((tech, index) => (
            <div 
              key={index} 
              className={styles.techCard}
              data-animate
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className={styles.techIcon}>{tech.icon}</div>
              <h3 className={styles.techName}>{tech.title}</h3>
              <p className={styles.techDescription}>{tech.description}</p>
              <div className={styles.techTags}>
                <span className={styles.techTag}>{tech.technologies}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* --- Featured Services Section with Enhanced Cards --- */}
      <section className={`${styles.section} ${styles.servicesSection}`} ref={el => sectionRefs.current[3] = el}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Our Services</span>
          <h2 className={styles.sectionTitle}>Your Blueprint for Digital Excellence</h2>
          <p className={styles.sectionDescription}>
            Cutting-edge solutions designed to transform your business and drive unprecedented growth
          </p>
        </div>
        <div className={styles.servicesGrid}>
          {featuredServices.map((service, index) => (
            <Link 
              key={service.id} 
              to={`/services/${service.id}`} 
              className={styles.cardLink}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <ServiceCard
                icon={service.icon}
                title={service.title}
                description={service.tagline}
              />
            </Link>
          ))}
        </div>
        <div className={styles.viewAllContainer}>
          <Link to="/services" className={styles.viewAllButton}>
            View All Services →
          </Link>
        </div>
      </section>

      {/* --- How We Work Process Section --- */}
      <section className={`${styles.section} ${styles.processSection}`} ref={el => sectionRefs.current[4] = el}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Our Process</span>
          <h2 className={styles.sectionTitle}>How We Transform Ideas Into Reality</h2>
          <p className={styles.sectionDescription}>
            A proven methodology that ensures successful project delivery from concept to deployment
          </p>
        </div>
        <div className={styles.processSteps}>
          {[
            { 
              number: '01', 
              title: 'Discovery & Planning', 
              description: 'We dive deep into your business needs, challenges, and goals to create a comprehensive project roadmap.',
              icon: '🔍'
            },
            { 
              number: '02', 
              title: 'Design & Architecture', 
              description: 'Our architects design scalable, secure solutions tailored to your specific requirements and future growth.',
              icon: '🏗️'
            },
            { 
              number: '03', 
              title: 'Development & Testing', 
              description: 'Agile development with continuous testing ensures high-quality code and early issue detection.',
              icon: '⚙️'
            },
            { 
              number: '04', 
              title: 'Deployment & Support', 
              description: 'Smooth deployment with comprehensive documentation and ongoing support to ensure your success.',
              icon: '🚀'
            },
          ].map((step, index) => (
            <div 
              key={index} 
              className={styles.processStep}
              data-animate
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className={styles.stepNumber}>{step.number}</div>
              <div className={styles.stepIcon}>{step.icon}</div>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepDescription}>{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* --- The SolveDot Labs Advantage Section with Enhanced Design --- */}
      <section className={`${styles.section} ${styles.advantageSection}`} ref={el => sectionRefs.current[5] = el}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Why Choose Us</span>
          <h2 className={styles.sectionTitle}>The Pillars of Our Partnership</h2>
        </div>
        <div className={styles.advantageGrid}>
          <div className={styles.advantageCard}>
            <div className={styles.advantageIcon}>💡</div>
            <h3>Unbreakable Quality</h3>
            <p>Every project is a masterpiece of code, guaranteeing flawless performance that you can build your business on.</p>
          </div>
          <div className={styles.advantageCard}>
            <div className={styles.advantageIcon}>🚀</div>
            <h3>Almost Zero Downtime Guarantee</h3>
            <p>Your operations never sleep, and neither does our software. We promise 100% uptime, so you're always open for business.</p>
          </div>
          <div className={styles.advantageCard}>
            <div className={styles.advantageIcon}>🤝</div>
            <h3>Your Vision, Amplified</h3>
            <p>We don't just build software; we build your future. We are your dedicated partners in innovation and growth.</p>
          </div>
        </div>
      </section>

      {/* --- Innovation Showcase Section --- */}
      <section className={`${styles.section} ${styles.innovationSection}`} ref={el => sectionRefs.current[6] = el}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Innovation</span>
          <h2 className={styles.sectionTitle}>Driving the Future of Technology</h2>
          <p className={styles.sectionDescription}>
            We're at the forefront of technological innovation, constantly pushing boundaries and exploring new possibilities
          </p>
        </div>
        <div className={styles.innovationGrid}>
          {[
            { 
              icon: '🤖', 
              title: 'AI & Machine Learning', 
              description: 'Harnessing the power of AI to create intelligent solutions that learn and adapt',
              color: '#007BFF'
            },
            { 
              icon: '☁️', 
              title: 'Cloud-Native Architecture', 
              description: 'Building scalable, resilient systems that grow with your business',
              color: '#00BFFF'
            },
            { 
              icon: '🔒', 
              title: 'Enterprise Security', 
              description: 'Fortifying your digital assets with cutting-edge security protocols',
              color: '#007BFF'
            },
            { 
              icon: '📊', 
              title: 'Data-Driven Insights', 
              description: 'Transforming raw data into actionable intelligence for strategic decisions',
              color: '#00BFFF'
            },
            { 
              icon: '⚡', 
              title: 'Performance Optimization', 
              description: 'Delivering lightning-fast applications that exceed user expectations',
              color: '#007BFF'
            },
            { 
              icon: '🌐', 
              title: 'Global Scalability', 
              description: 'Solutions designed to scale seamlessly across markets and regions',
              color: '#00BFFF'
            },
          ].map((innovation, index) => (
            <div 
              key={index} 
              className={styles.innovationCard}
              data-animate
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={styles.innovationIconWrapper}>
                <div className={styles.innovationIconGlow}></div>
                <div className={styles.innovationIcon}>{innovation.icon}</div>
              </div>
              <h3 className={styles.innovationTitle}>{innovation.title}</h3>
              <p className={styles.innovationDescription}>{innovation.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* --- Enhanced Testimonials Section --- */}
      <section className={`${styles.section} ${styles.testimonialSection}`} ref={el => sectionRefs.current[7] = el}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Testimonials</span>
          <h2 className={styles.sectionTitle}>Trusted by Industry Innovators</h2>
        </div>
        <div className={styles.sliderContainer}>
          <Slider {...sliderSettings} className={styles.testimonialSlider}>
            {testimonialsData.map((testimonial, index) => (
              <div key={index} className={styles.testimonialWrapper}>
                <TestimonialCard
                  quote={testimonial.quote}
                  author={testimonial.author}
                  company={testimonial.company}
                />
              </div>
            ))}
          </Slider>
        </div>
      </section>
      
      {/* --- Enhanced Final CTA Section --- */}
      <section className={`${styles.section} ${styles.finalCta}`} ref={el => sectionRefs.current[8] = el}>
        <div className={styles.ctaContent}>
          <h2>Ready to Build Your Unstoppable Future?</h2>
          <p>Let's talk about the challenges you're facing and how our technology can solve them. The first consultation is on us.</p>
          <Link to="/contact" className={styles.ctaButton}>
            <span>Book a Free Consultation</span>
            <span className={styles.arrow}>→</span>
          </Link>
        </div>
        <div className={styles.ctaPattern}></div>
      </section>
    </div>
  );
};

export default HomePage;
